<template>
  <div>
    <div class="layout document-form">
      <u-confirm id="delete-doctype" ref="confirm" />
      <c-loader v-if="loading" label="" :active="loading" height="500" />

      <div v-else>
        <div class="layout-header">
          <div class="layout-title">
            {{ id ? "Редактирование документа" : "Новый документ" }}
          </div>
          <div class="layout-header-actions">
            <div v-if="id" :id="`layout-header-action-${id}`" class="layout-header-action">
              <c-button-2
                size="small"
                label="Удалить"
                :icon="{
                  icon: 'trash',
                  color: 'secondary',
                }"
                :disabled="!savedData.canDelete"
                :responsive="true"
                @click="deleteDocument"
              />
            </div>

            <b-popover
              v-if="!savedData.canDelete"
              placement="top"
              triggers="hover blur"
              :target="`layout-header-action-${id}`"
            >
              <error-feedback
                class="m-0"
                text="В данный момент удаление невозможно, так как документ добавлен для сотрудников."
              ></error-feedback>
            </b-popover>

            <div class="layout-header-action">
              <c-button-2
                size="small"
                label="К списку документов"
                :icon="{
                  icon: 'back',
                  color: 'blue',
                }"
                :responsive="true"
                @click="$router.push({ name: 'documents-list' })"
              />
            </div>
          </div>
        </div>
        <div>
          <div class="document-form__name">
            <c-input-2
              v-model="form.name"
              floating-label="Название документа"
              :style="{
                'margin-bottom': submitted && nameError ? '40px' : '',
              }"
              :required-mark="true"
              :required="submitted && nameError"
              :required-message="submitted && nameError"
              :clearable="true"
              :max-length="255"
            />
          </div>

          <div class="document-form__type">
            <c-input-2
              v-model="form.type"
              floating-label="Тип документа"
              type="select"
              :items="doctypesDictionary"
              :clearable="true"
              :searchable="true"
            />
          </div>

          <div class="document-form__statuses">
            <c-input-2
              v-model="form.statuses"
              floating-label="Правовой статус"
              type="select"
              class="document-form__statuses__law-status"
              :multiple="true"
              :items="statusesDictionary"
              :searchable="true"
            />
            <c-input-2
              v-model="form.gender"
              label="Пол"
              type="select"
              class="document-form__statuses__gender"
              placeholder="Для всех"
              :items="genderTypes"
              :clearable="true"
            />
          </div>

          <div class="document-form-issues">
            <h2>Проблемы с документами</h2>
            <div class="document-form-criticality">
              <c-input-2
                v-model="form.criticality"
                class="document-form-criticality__value"
                label="Критичность документа"
                type="select"
                :items="criticalityTypes"
              />

              <document-color
                v-if="form.criticality.value !== 'optional'"
                ref="absentColor"
                v-model="form.absentColor"
                class="document-form-criticality__color"
                label="Цвет отсутствующего документа"
                default-color="#FF0000"
                :validate="submitted"
              ></document-color>
            </div>
            <div>
              <div class="d-inline-block">
                <c-switcher
                  v-model="form.hasExpireDate"
                  label="Срок годности документа"
                  label-full-name="Срок годности документа"
                />
              </div>
            </div>
            <div v-if="form.hasExpireDate">
              <div class="document-form-expiration">
                <div class="document-form-expiration-indication">
                  <div
                    class="document-form-expiration-indication__field"
                    :style="{
                      'margin-bottom': submitted && indicationError ? '30px' : '',
                    }"
                  >
                    <c-input-2
                      v-model="form.indication"
                      label="Индикация проблемы за"
                      :required-mark="true"
                      :required="submitted && indicationError"
                      :required-message="submitted && indicationError"
                      :mask="['###']"
                      :clearable="true"
                    />
                    <span class="document-form-expiration-indication__label">дней</span>
                  </div>

                  <div class="document-form-expiration-indication__note">
                    Если нужно уведомлять о возникновении проблемы при вышедшем сроке, ставьте 0 дней
                  </div>
                </div>

                <document-color
                  ref="expiredColor"
                  v-model="form.expiredColor"
                  class="document-form-expiration__color"
                  label="Цвет истекшего срока годности"
                  :validate="submitted"
                  default-color="#FF238D"
                ></document-color>

                <document-color
                  ref="expiringColor"
                  v-model="form.expiringColor"
                  class="document-form-expiration__color"
                  label="Цвет истекающего срока годности"
                  :validate="submitted"
                  default-color="#FFB800"
                ></document-color>
              </div>

              <div>
                <div class="d-inline-block">
                  <c-switcher
                    v-model="form.salaryLock"
                    label="Блокировка зарплаты"
                    label-full-name="Блокировка зарплаты"
                  />
                </div>
              </div>

              <c-input-2
                v-if="form.salaryLock"
                v-model="form.blockingConditions"
                class="document-form__salary-conditions"
                label="Условие блокировки"
                :required-mark="true"
                :required="submitted && salaryLockError"
                :required-message="submitted && salaryLockError"
                placeholder="Все"
                type="select"
                :multiple="true"
                :items="blockingConditionsDictionary"
              />
            </div>
          </div>

          <section class="document-form-conditions">
            <header class="document-form-conditions__header">
              <toggle-button v-model="conditionsIsOpen" />
              <h2>
                Условия применения
                <span class="count">{{ filledConditions.length || "Для всех" }}</span>
              </h2>
            </header>
            <slide-up-down :active="conditionsIsOpen" :duration="400" class="document-form-conditions__items">
              <div class="document-conditions">
                <document-condition
                  v-for="(condition, i) in form.conditions"
                  :key="i"
                  class="document-conditions__item"
                  :condition="condition"
                  :validate="submitted"
                  @remove="removeCondition(condition)"
                ></document-condition>
              </div>
              <div class="document-form-conditions__add">
                <c-button
                  :disabled="hasEmptyConditions"
                  label="Добавить условие"
                  :icon="{
                    icon: 'plus',
                    color: 'blue',
                  }"
                  @click="addCondition"
                />
              </div>
            </slide-up-down>
          </section>
          <section class="document-form-notifications">
            <header class="document-form-notifications__header">
              <toggle-button v-model="notificationsIsOpen" />
              <h2>
                Уведомления
                <span class="count">{{ filledNotifications.length || "Нет" }}</span>
              </h2>
            </header>
            <slide-up-down :active="notificationsIsOpen" :duration="400" class="document-form-notifications__items">
              <div class="document-notifications">
                <document-notification
                  v-for="(notification, i) in form.notifications"
                  :key="i"
                  class="document-notifications__item"
                  :validate="submitted"
                  :notification="notification"
                  @remove="removeNotification(notification)"
                ></document-notification>
              </div>
              <div class="document-form-conditions__add">
                <c-button
                  :disabled="hasEmptyNotifications"
                  label="Добавить уведомление"
                  :icon="{
                    icon: 'plus',
                    color: 'blue',
                  }"
                  @click="addNotification"
                />
              </div>
            </slide-up-down>
          </section>
        </div>
        <div class="document-form__actions">
          <c-button-2 size="large" class="document-form__reset" label="Отменить" @click="reset" />
          <c-button-2
            class="document-form__save"
            label="Сохранить"
            color="primary"
            size="large"
            :disabled="pending"
            @click="submit"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import SlideUpDown from "vue-slide-up-down";
import ToggleButton from "@/components/controls/toggle-button";
import Button from "@/components/controls/button";
import Button2 from "@/components/controls2/Button";
import Input2 from "@/components/controls2/Input";
import Switcher from "@/components/controls/switcher";
import loader from "@/components/loader";
import Confirm from "@/pages/documents/u-confirm";
import DocumentColor from "./document-color";
import DocumentCondition from "./document-condition";
import DocumentNotification from "./document-notification";
import ErrorFeedback from "@/components/error-feedback";

function formatCondition(condition = {}, server = "") {
  if (server) {
    if (!condition.citizenships.length && !condition.positions.length) {
      return {};
    }
    const formattedCondition = {
      citizenship_limit: condition.citizenshipsExcept ? 0 : 1, // TODO
    };
    if (condition.citizenships.length) {
      formattedCondition.citizenship_ids = condition.citizenships.map((i) => i.value);
    }
    if (condition.positions.length) {
      formattedCondition.posts_codes = condition.positions.map((c) => c.value);
    }
    return formattedCondition;
  }


  return {
    citizenships: (condition.citizenships || []).map((c) => ({ value: c })),
    positions: (condition.posts || []).map((p) => ({ value: p })),
    citizenshipsExcept: condition.citizenship_limit==undefined ? 0 : !condition.citizenship_limit,
  };
}

function formatNotification(notification = {}, server = "") {
  if (server) {
    console.log(notification)
     if (!notification.user) {
      return {};
    }
    return {
      filial_ids: notification.filials.map((f) => f.value),
      is_email: notification.email,
      is_sms: notification.sms,
      user_id: notification.user.value,
    };
  }
  return {
    filials: (notification.filials || []).map((f) => ({ value: f })),
    user: notification.user_id ? { value: notification.user_id } : null,
    email: notification.is_email,
    sms: notification.is_sms,
  };
}

export default {
  name: "document-form",
  components: {
    "slide-up-down": SlideUpDown,
    "c-button": Button,
    "c-button-2": Button2,
    "c-input-2": Input2,
    "c-switcher": Switcher,
    "c-loader": loader,
    "u-confirm": Confirm,
    "toggle-button": ToggleButton,
    "document-color": DocumentColor,
    "document-condition": DocumentCondition,
    "document-notification": DocumentNotification,
    ErrorFeedback,
  },

  data() {
    return {
      loading: true,
      submitted: false,
      pending: false,

      criticalityTypes: [
        { label: "Необязательный", value: "optional", disabled: false },
        { label: "Обязательный", value: "required" },
        { label: "Критичный", value: "critical" },
      ],
      blockingConditionsDictionary: [
        { label: "Документ не загружен", value: "not_loaded" },
        { label: "Срок годности прошёл", value: "expired" },
        { label: "Срок годности подходит", value: "expiring" },
      ],
      genderTypes: [
        { label: "Мужской", value: "male" },
        { label: "Женский", value: "female" },
      ],
      conditionsIsOpen: false,
      notificationsIsOpen: false,

      id: "",

      form: {
        name: "",
        type: "",
        statuses: [],
        criticality: { value: "optional" },
        absentColor: "",
        hasExpireDate: false,
        indication: 10,
        expiredColor: "",
        expiringColor: "",
        salaryLock: false,
        blockingConditions: [],
        conditions: [],
        notifications: [],
        gender: null,
      },

      validation: {
        conditions: {
          user: "", // required if filials
        },
      },

      savedData: {
        indication: 10,
        criticality: { value: "optional" },
      },
    };
  },

  computed: {
    ...mapState("documentForm", [
      "doctypesDictionary",
      "statusesDictionary",
      "usersDictionary",
      "citizenshipsDictionary",
    ]),

    hasStatus() {
      return this.form.statuses.length;
    },

    filledConditions() {
      return this.form.conditions.filter((c) => !this.isConditionEmpty(c));
    },

    hasEmptyConditions() {
      return this.form.conditions.length > this.filledConditions;
    },

    filledNotifications() {
      return this.form.notifications.filter((n) => !this.isNotificationEmpty(n));
    },

    hasEmptyNotifications() {
      return this.form.notifications.length > this.filledNotifications;
    },

    nameError() {
      return !this.form.name.trim().length ? "Обязательное поле" : "";
    },

    salaryLockError() {
      return this.form.salaryLock (!this.form.blockingConditions || !this.form.blockingConditions.length) ? "Обязательное поле" : "";
    },

    indicationError() {
      if (!this.form.hasExpireDate) return "";

      return this.form.indication || this.form.indication === "0" ? "" : "Обязательное поле";
    },

    blockingConditionsError() {
      return this.form.salaryLock && !this.form.blockingConditions.length ? "Обязательное поле" : "";
    },
  },

  watch: {
    "form.hasExpireDate" : function (v) {

      if (v && !this.form.indication) {
        this.form.indication=10;
      }
    },

    hasStatus(v) {
      if (v) {
        this.criticalityTypes[0].disabled = true;
      } else {
        this.criticalityTypes[0].disabled = false;

      }
      if (v && this.form.criticality.value === "optional") {
        this.form.criticality = { value: "required" };
      } else {
      }
    },
  },

  created() {
    this.$store.dispatch("documentForm/loadDoctypes");
    this.$store.dispatch("documentForm/loadStatuses");
    this.$store.dispatch("documentForm/loadCitizenships");
    this.$store.dispatch("documentForm/loadUsers");

    const { id } = this.$route.params;
    this.id = id;
    if (id) {
      this.$store.dispatch("documentForm/loadDocument", { id }).then((data) => {
        this.savedData = data;
        this.setData(this.savedData);
        this.loading = false;
      });
    } else this.loading = false;
  },

  methods: {
    getData() {
      const document = {};
      document.name = this.form.name;
      document.doc_group_id = this.form.type ? this.form.type.value : "";
      document.law_statuses = this.form.statuses.map((s) => s.value);
      document.criticality = this.form.criticality.value;
      document.absent_color = this.form.absentColor;
      document.has_expire_date = this.form.hasExpireDate ? 1 : 0;
      document.indication = this.form.indication;
      document.expired_color = this.form.expiredColor;
      document.expiring_color = this.form.expiringColor;
      document.salary_lock = this.form.salaryLock ? 1 : 0;
      document.blocking_condition = this.form.blockingConditions.map((c) => c.value);
      document.conditions = this.form.conditions.map((c) => formatCondition(c, "server"));
      document.notifications = this.form.notifications.map((n) => formatNotification(n, "server"));

      if (this.form.gender) {
        document.gender = this.form.gender.value;
      } else {
        document.gender = null;
      }


      return document;
    },
    setData(data = {}) {
      this.form.name = data.name || "";
      this.form.type = data.doc_group ? { value: data.doc_group } : null;

      this.form.statuses = (data.law_statuses || []).map((s) => ({ value: s }));

      const criticality = { value: "optional" };
      if (data.critical) criticality.value = "critical";
      else if (data.required) criticality.value = "required";
      this.form.criticality = criticality;
      this.form.absentColor = data.absent_color || "";

      this.form.hasExpireDate = !!data.has_expire_date;
      this.form.indication = data.indication;
      this.form.expiredColor = data.expired_color || "";
      this.form.expiringColor = data.expiring_color || "";

      this.form.salaryLock = !!data.salary_lock;

      this.form.blockingConditions = (data.blocking_condition || []).map((c) => ({
        value: c,
      }));

      this.form.conditions = (data.conditions || []).map((c) => formatCondition(c));
      this.form.notifications = (data.notifications || []).map((c) => formatNotification(c));

      this.form.gender = data.gender ? this.genderTypes.find((type) => data.gender === type.value) : null;
    },
    reset() {
      this.setData(this.savedData);
    },

    validate() {
      let isValid = true;

      if (this.nameError) isValid = false;
      if (this.indicationError) isValid = false;
      if (this.blockingConditionsError) isValid = false;

      ["absentColor", "expiringColor", "expiredColor"].forEach((color) => {
        const ref = this.$refs[color];
        if (ref && !ref.isValid) isValid = false;
      });

      this.form.notifications.forEach((n) => {
        if (!n.filials.length) return;
        if (!n.user || !n.user.value) isValid = false;
      });

      return isValid;
    },
    submit() {
      this.submitted = true;
      if (!this.validate()) return false;

      this.submitted = false;
      this.pending = true;

      const data = this.getData();

      if (this.id) {
        this.$store
          .dispatch("documentForm/updateDocument", {
            id: this.id,
            data,
          })
          .then((data) => {
            if (data.success) {
              this.$router.push({ name: "documents-list" });
              return;
            }
            this.savedData = this.form;
            this.pending = false;
          });
      } else {
        this.$store.dispatch("documentForm/createDocument", { data }).then((data) => {
          if (data.success) {
            this.$router.push({ name: "documents-list" });
            return;
          }
          this.savedData = this.form;
          this.id = data.id;
          this.$router.replace({
            name: "edit-document",
            params: { id: data.id },
          });
          this.pending = false;
        });
      }
      return true;
    },

    addCondition() {
      console.log(formatCondition())
      this.form.conditions = [...this.form.conditions, formatCondition()]; 
    },
    removeCondition(condition) {
      const conditions = this.form.conditions.filter((c) => c !== condition);
      this.form.conditions = conditions;
    },
    isConditionEmpty(condition) {
      return !condition.citizenships.length && !condition.positions.length;
    },
    addNotification() {
      this.form.notifications = [...this.form.notifications, formatNotification()];
    },
    removeNotification(notification) {
      const notifications = this.form.notifications.filter((n) => n !== notification);
      this.form.notifications = notifications;
    },
    isNotificationEmpty(notification) {
      return (
        (!notification.user || !notification.user.value) &&
        !notification.filials.length &&
        !notification.sms &&
        !notification.email
      );
    },
    deleteDocument() {
      this.$refs.confirm.open(
        {
          title: "Удаление документа",
          text: `Документ <b>${this.savedData.name}</b> будет удалён без возможности восстановления`,
        },
        () => {
          this.$store.dispatch("documents/removeDocument", { id: this.id }).then(() => {
            this.$router.push({ name: "documents-list" });
          });
        },
      );
    },
  },
};
</script>

<style lang="scss">
@import "~@/assets/styles/vars";

.document-form {
  @include mobileAndTablet {
    padding: 40px 30px 100px 30px !important;
  }
  @include onlyExtraSmall {
    padding: 32px 20px 70px 20px !important;
  }

  &__name,
  &__type {
    margin-bottom: 24px;
  }

  &__name,
  &__statuses {
    max-width: 763px;
  }

  &__statuses {
    display: flex;

    @include onlyExtraSmall {
      flex-direction: column;
    }

    &__law-status {
      flex-grow: 1;
      @include onlyExtraSmall {
        margin-bottom: 24px;
      }
    }
    &__gender {
      @media (min-width: 421px) {
        margin-left: 30px;
        width: 154px;
      }
      @media (min-width: 992px) {
        width: 169px;
      }
    }
  }

  &__type {
    max-width: 366px;
  }

  &-issues {
    margin-top: 40px;

    h2 {
      font-size: 18px;
      margin-bottom: 20px;
      @include onlyExtraSmall {
        font-size: 15px;
      }
    }
  }

  &-criticality {
    display: flex;
    margin-bottom: 30px;

    @include onlyExtraSmall {
      flex-direction: column;
    }

    &__value {
      width: 266px;
      margin-right: 30px;

      @include mobileAndTablet {
        width: 216px;
      }
      @include onlyExtraSmall {
        width: auto;
        margin-right: 0;
        margin-bottom: 25px;
      }
    }
  }

  &-expiration {
    display: flex;
    margin-top: 24px;
    margin-bottom: 30px;

    @include onlyExtraSmall {
      flex-direction: column;
      margin-bottom: 0;
    }

    &-indication {
      width: 266px;
      margin-right: 30px;

      @include mobileAndTablet {
        width: 216px;
      }
      @include onlyExtraSmall {
        margin-bottom: 25px;
      }

      &__field {
        display: flex;
        align-items: center;

        .c-input-action_clear {
          margin-top: -3px;
        }
      }
      &__label {
        margin-left: 15px;
        margin-top: 11px;
        font-size: 15px;
      }
      &__note {
        color: $color-label;
        font-size: 11px;
        margin-top: 8px;
        line-height: 1.2;
      }
    }

    &__color {
      margin-right: 30px;

      @include onlyExtraSmall {
        margin-bottom: 25px;
      }
    }
  }

  &__salary-conditions {
    margin-top: 24px;
    max-width: 765px;

    @include onlyExtraSmall {
      margin-top: 18px;
      margin-bottom: 30px;
    }
  }

  &-conditions,
  &-notifications {
    border-top: 1px solid $color-border-light;

    @include onlyExtraSmall {
      width: 100vw;
      margin-left: -20px;
      padding: 0 20px;
    }

    &__header {
      display: flex;
      align-items: center;
      padding-top: 12px;
      padding-bottom: 12px;

      @include onlyExtraSmall {
        padding-top: 20px;
        padding-bottom: 20px;
      }

      h2 {
        font-size: 18px;
        font-weight: 700;
        margin-bottom: 0;
        margin-left: 16px;
        @include onlyExtraSmall {
          font-size: 15px;
        }
      }

      .count {
        font-weight: 400;
        color: $color-label;
        margin-left: 8px;
      }
    }

    &__add {
      padding-bottom: 25px;
    }
  }

  &-conditions {
    margin-top: 40px;

    @include onlyExtraSmall {
      margin-top: 20px;
    }

    .document-conditions {
      padding-top: 22px;
      @include onlyExtraSmall {
        padding-top: 8px;
      }
    }

    .document-conditions__item + .document-conditions__item {
      margin-top: 40px;
    }

    .document-conditions__item:last-child {
      margin-bottom: 26px;
    }
  }

  &-notifications {
    border-bottom: 1px solid $color-border-light;

    .document-notifications {
      padding-top: 22px;
      @include onlyExtraSmall {
        padding-top: 8px;
      }
    }

    .document-notifications__item + .document-notifications__item {
      margin-top: 40px;
    }

    .document-notifications__item:last-child {
      margin-bottom: 26px;
    }
  }

  &__actions {
    margin-top: 40px;
    display: flex;
  }

  &__reset {
    margin-right: 10px;
    width: 120px;
    padding: 0;
    background: #fff;
  }
  &__save {
    width: 125px;
    padding: 0;
  }
}
@media (max-width: 480px) {
  .layout {
    margin: 0;
  }
  .document-form {
    &__actions {
      position: fixed;
      bottom: 0;
      left: 0;
      width: 100%;
    }
    &__actions button {
      width: 50%;
      margin: 0;
      border-radius: 0 !important;
    }
    &__reset {
      background: #fff !important;
    }
  }
}
</style>
